import { useTranslation } from 'react-i18next'

import { formatNumber, NumberFormats } from '@/shared/tools/locale'

import { LEGEND_LABELS } from '../lib/labels'
import { formatDate } from '../lib/period'

type SeriesKey = 'actual' | 'median' | 'band50' | 'band90'

type FanTooltipItem = {
	dataKey?: string | number
	value?: number | [number, number] | null
}

// fixed display order + labels/swatches matching the chart legend
const SERIES: { key: SeriesKey; swatch: string }[] = [
	{ key: 'actual', swatch: 'bg-muted-foreground' },
	{ key: 'median', swatch: 'bg-primary' },
	{ key: 'band50', swatch: 'bg-primary/30' },
	{ key: 'band90', swatch: 'bg-primary/15' },
]

const formatValue = (value: number | [number, number]) =>
	Array.isArray(value)
		? `${formatNumber(value[0], NumberFormats.Integer)} – ${formatNumber(value[1], NumberFormats.Integer)}`
		: formatNumber(value, NumberFormats.Integer)

// Rendered by recharts' Tooltip, which injects active/payload/label at runtime.
export const ForecastFanTooltip = ({
	active,
	payload,
	label,
}: {
	active?: boolean
	payload?: FanTooltipItem[]
	label?: unknown
}) => {
	const { t } = useTranslation()

	if (!active || !payload?.length) {
		return null
	}

	const byKey = new Map(payload.map((item) => [item.dataKey, item.value]))
	const rows = SERIES.flatMap(({ key, swatch }) => {
		const value = byKey.get(key)

		if (value === null || value === undefined) {
			return []
		}

		return [
			{
				key,
				label: t(LEGEND_LABELS[key].key, {
					defaultValue: LEGEND_LABELS[key].defaultValue,
				}),
				swatch,
				value,
			},
		]
	})

	if (!rows.length) {
		return null
	}

	return (
		<div
			data-testid='mtf-fan-tooltip'
			className='grid min-w-[10rem] gap-1.5 rounded-lg border border-border/50 bg-foreground px-2.5 py-1.5 text-xs text-background shadow-xl'
		>
			<div className='font-medium'>
				{typeof label === 'string' ? formatDate(label) : ''}
			</div>
			<div className='grid gap-1'>
				{rows.map(({ key, label: rowLabel, swatch, value }) => (
					<div
						key={key}
						data-testid={`mtf-fan-tooltip-row-${key}`}
						className='flex w-full items-center gap-2'
					>
						<span className={`size-2.5 shrink-0 rounded-[2px] ${swatch}`} />
						<span className='flex-1 text-background/50'>{rowLabel}</span>
						<span className='font-mono font-medium tabular-nums'>
							{formatValue(value)}
						</span>
					</div>
				))}
			</div>
		</div>
	)
}
